import { gql } from '@graphql/__generated__';

// =============================================================================
// QUERIES
// =============================================================================

// Get listing MLS settings (fast - lot data only)
export const GET_LISTING_MLS_SETTINGS = gql(/* GraphQL */ `
  query GetListingMlsSettings($lotId: uuid!) {
    lot: lots_by_pk(id: $lotId) {
      id
      shared_on_mls_at
      mls_share_mode
      mls_terms_enabled
      mls_commission_type
      mls_buyer_agent_share_percent
      mls_buyer_agent_share_fixed_amount
      mls_note
      currency
      broker {
        id
        teams_users {
          team_id
        }
      }
      # Get portal listings by team for "Published on website" column
      portal_listings(
        where: { live: { _eq: true }, portal: { type: { _in: [idx, shared] } } }
      ) {
        id
        portal {
          id
          team_id
        }
      }
    }
    listing_shares: mls_shares(
      where: { lot_id: { _eq: $lotId }, status: { _eq: "shared" } }
      order_by: { created_at: desc }
    ) {
      id
      lot_id
      team_id
      shared_by_user_id
      created_at
      team {
        id
        name
      }
    }
    share_requests: mls_shares(
      where: {
        lot_id: { _eq: $lotId }
        status: { _in: ["pending", "rejected"] }
      }
      order_by: { created_at: desc }
    ) {
      id
      lot_id
      team_id
      requested_by_user_id
      requester_name
      status
      handled_at
      created_at
      team {
        id
        name
      }
    }
  }
`);

// Get team details for shared teams via cross-tenant-safe view
export const GET_MLS_SHARED_TEAM_DETAILS = gql(/* GraphQL */ `
  query GetMlsSharedTeamDetails($teamIds: [uuid!]!) {
    shared_teams: mls_network_teams(where: { id: { _in: $teamIds } }) {
      id
      name
      logo_url
      team {
        id
        locality
        postcode
      }
    }
  }
`);

// Get network teams with server-side pagination
export const GET_MLS_NETWORK_TEAMS = gql(/* GraphQL */ `
  query GetMlsNetworkTeams(
    $where: mls_network_teams_bool_exp!
    $countryCode: String!
    $limit: Int!
    $offset: Int!
    $orderBy: [mls_network_teams_order_by!] = [{ name: asc }]
  ) {
    network_teams: mls_network_teams(
      where: $where
      limit: $limit
      offset: $offset
      order_by: $orderBy
    ) {
      id
      name
      country_code
      broker_count
      active_mls_listings_count
      logo_url
      team {
        id
        locality
        postcode
        places {
          id
        }
      }
    }
    network_teams_aggregate: mls_network_teams_aggregate(where: $where) {
      aggregate {
        count
      }
    }
    # Total count without search/place filters (for Members stat)
    network_teams_total: mls_network_teams_aggregate(
      where: { country_code: { _eq: $countryCode } }
    ) {
      aggregate {
        count
      }
    }
  }
`);

// Browse MLS listings (main MLS page)
export const GET_MLS_LOTS = gql(/* GraphQL */ `
  query GetMls(
    $limit: Int
    $offset: Int
    $where: mls_lots_bool_exp
    # id breaks ties, as in GetMlsLotsMap: without it a batch sharing one
    # timestamp can repeat a lot on one page and skip it on the next.
    $order_by: [mls_lots_order_by!] = [
      { shared_on_mls_at: desc_nulls_last }
      { id: asc }
    ]
  ) {
    mls_lots_aggregate(where: $where) {
      aggregate {
        count
      }
    }
    mls_lots(
      limit: $limit
      offset: $offset
      order_by: $order_by
      where: $where
    ) {
      id
      created_at
      shared_on_mls_at
      title
      lot_reference
      offer_type
      sale_price
      rent_net
      rent_extra
      status
      currency
      hide_price_on_portals
      mls_share_mode
      mls_terms_enabled
      mls_commission_type
      mls_buyer_agent_share_percent
      access_status
      # Server-gated address: the only one an agent may see for this lot.
      # Replaces the raw property.route / street_number / locality /
      # postcode columns plus the dummy_* fallbacks the client used to
      # pick between — role agent is no longer granted those columns on
      # mls_properties. See modules/mls/effectiveAddress.ts.
      effective_address
      # Use portal_listings array like regular lots query
      portal_listings {
        id
        live
      }
      broker {
        id
        first_name
        last_name
        profile_image_url
        default_team {
          id
          name
          logo {
            url
          }
        }
      }
      property {
        id
        living_surface
        number_of_rooms
        property_images(order_by: { order_nr: asc }) {
          id
          image {
            id
            url
          }
        }
        property_type {
          id
          label
        }
        property_files(order_by: { order_nr: asc }) {
          ...PropertyFile
        }
      }
    }
  }
`);

// Slim query for the map view — only fields needed to place markers + render
// the rail card + compute buyer-lead matching.
//
// Address fields go through the `effective_address` computed field
// (mls_lot_effective_address SQL function): owner / shared rows return
// the real address, restricted rows return the dummy address when set
// or a quantised lat/lng (~1 km grid) with locality / postcode
// withheld. The raw `property.lat / lng / locality / postcode` columns
// are deliberately NOT selected here so the GraphQL response can't be
// inspected in DevTools to recover the real address of a restricted
// listing.
//
// "Hot" demand is the `hot_score` Hasura computed field on mls_lots,
// backed by the `mls_lot_hot_score(mls_lots)` SQL function. It sums
// each share's contribution exp(-LAMBDA * age_in_days) at query time
// (LAMBDA = ln(2)/14, 14-day half-life). Stateless — no triggers, no
// cron, no drift. OK while mls_shares stays small (<~50/lot); revisit
// with denormalisation if share volume grows.
export const GET_MLS_LOTS_MAP = gql(/* GraphQL */ `
  query GetMlsLotsMap(
    $limit: Int
    $where: mls_lots_bool_exp
    # id breaks ties: lots shared in one batch carry the same timestamp, and
    # without it the rail order and the rows the limit cuts vary per plan.
    $order_by: [mls_lots_order_by!] = [
      { shared_on_mls_at: desc_nulls_last }
      { id: asc }
    ]
    $minLng: float8!
    $minLat: float8!
    $maxLng: float8!
    $maxLat: float8!
    $countryCode: String
    $marketOn: Boolean! = false
  ) {
    # The viewport predicate lives in SQL: each access branch filters on the
    # coordinate that same branch discloses, so the bbox cannot be used to
    # probe a restricted listing's position. Aliased to mls_lots so the
    # view-model code downstream is unchanged.
    mls_lots: mls_lots_in_viewport(
      args: {
        min_lng: $minLng
        min_lat: $minLat
        max_lng: $maxLng
        max_lat: $maxLat
        p_country_code: $countryCode
      }
      limit: $limit
      order_by: $order_by
      where: $where
    ) {
      id
      shared_on_mls_at
      offer_type
      sale_price
      rent_net
      rent_extra
      status
      currency
      access_status
      effective_address
      # Commune the choropleth counts this lot in, never finer than
      # effective_address (mls_lot_market_place_id). A polygon lookup per
      # restricted lot, so only paid while the choropleth is on.
      market_place_id @include(if: $marketOn)
      # MLS sharing terms — surfaced on the peek card so the agent can
      # decide whether requesting access is worth it before clicking
      # through. share_mode distinguishes everyone listings from
      # individual (request required).
      mls_share_mode
      mls_commission_type
      mls_buyer_agent_share_percent
      mls_buyer_agent_share_fixed_amount
      # Computed at query time by the mls_lot_hot_score SQL function.
      # See mlsQueries header comment + the function definition in
      # 1779200000000_mls_map_perf/up.sql for the formula.
      hot_score
      broker_id
      property {
        id
        # No place foreign keys here any more. Read per listing they recover
        # the commune of a restricted lot, which is exactly what the address
        # gate withholds. The choropleth reads market_place_id instead.
        living_surface
        # Inputs to properties.computed_surface (a stored generated column
        # not exposed on the cross-tenant mls_properties view). buildListing
        # reconstructs computed_surface from these via the migration formula
        # (ES-first COALESCE order) to feed the market-pulse Price/m² metric,
        # so ES sale listings (surface in built_surface) aren't dropped. All
        # plain columns, negligible cost.
        built_surface
        usable_surface
        number_of_rooms
        property_type {
          id
          name
          label
          main_type
          composite_type_object {
            id
            name
          }
        }
        property_images(order_by: { order_nr: asc }, limit: 1) {
          id
          image {
            id
            url
          }
        }
      }
      broker {
        id
        first_name
        last_name
        profile_image_url
        default_team {
          id
          name
          logo {
            url
          }
        }
      }
    }
  }
`);

// Minimal query used by the initial-bbox cascade step 3: fetch the agent's
// own owner + shared listings' coordinates so the map opens centered on
// "where my work lives". Separate from the main map query so it can run
// once on first open and be cached.
export const GET_MLS_AGENT_BBOX = gql(/* GraphQL */ `
  query GetMlsAgentBbox($where: mls_lots_bool_exp!, $limit: Int!) {
    mls_lots(where: $where, limit: $limit) {
      id
      # The caller filters to owner + shared listings, so this resolves to
      # the real coordinate; going through the gate anyway keeps a widened
      # where from silently reading restricted coords.
      effective_address
    }
  }
`);

// Server-authoritative buyer↔lot match edges for the viewport. Calls the
// mls_agent_buyer_matches SQL function (single source of truth, shared with the
// matching drawer) so the map's ★N badge counts exactly what the drawer lists.
// Session-scoped to "your buyers" inside the function; the client only passes
// the viewport bbox and aggregates the returned (lot_id, buyer_user_id) edges
// into per-lot counts, the buyer→lots inverse, and the choropleth demand.
export const GET_MLS_VIEWPORT_MATCHES = gql(/* GraphQL */ `
  query GetMlsViewportMatches($lotIds: _uuid!) {
    mls_agent_buyer_matches(args: { lot_ids: $lotIds }) {
      lot_id
      buyer_user_id
      property_search_id
    }
  }
`);

// Agent's active buyer-lead property searches. Still fetched for the buyer
// OVERLAY (pins, place geometry, tooltips) — the match COUNTS now come from
// GET_MLS_VIEWPORT_MATCHES above, not from matching these client-side.
// Scope: searches owned by buyers who have a buyer lead assigned to the
// current agent (by broker_id) or to a team the agent belongs to. The
// caller (useStrictBuyerMatch) additionally gates the `where` on
// `created_at ≥ 1yr` and `notification ∈ [daily, weekly]` — the same
// recency + notification filter the drawer's default matching filters
// apply — so the ★N badge counts the same active buyers the drawer does.
// Capped at 500 — enough for any realistic book of business.
//
// `$includeGeometry` gates `places.simplified_geom` — a PostGIS polygon
// payload that is large (5–20 KB per canton-level place, ×500 searches ≈
// up to 30 MB) and only consumed by the buyer-overlay's place-shape
// rendering. The strict matcher itself only needs `place.id` for its
// place-hierarchy intersection. We fetch the geometry lazily once the
// agent toggles the buyer overlay on; default `false` so the map-open
// case stays slim.
export const GET_AGENT_PROPERTY_SEARCHES = gql(/* GraphQL */ `
  query GetAgentPropertySearches(
    $where: property_searches_bool_exp!
    $limit: Int!
    $includeGeometry: Boolean = false
  ) {
    property_searches(where: $where, limit: $limit) {
      id
      user_id
      offer_type
      sale_price_lte
      sale_price_gte
      monthly_rent_lte
      monthly_rent_gte
      living_surface_lte
      living_surface_gte
      number_of_rooms_lte
      number_of_rooms_gte
      composite_property_type_eq
      property_searches_places {
        place_id
        place {
          id
          name
          lat
          lng
          # PostGIS GeoJSON polygon of the place's actual shape. Used to
          # outline the buyer's declared search area at its real
          # boundary (canton-shaped, locality-shaped …) instead of an
          # arbitrary radius circle or a bbox rectangle.
          simplified_geom @include(if: $includeGeometry)
        }
      }
      user {
        id
        first_name
        last_name
      }
    }
  }
`);

// Slim geometry lookup for the map's market-pulse choropleth: the
// viewport's distinct municipality/locality ids → their polygon +
// display name. Separate from the listings query so the (heavy)
// PostGIS payload is only fetched while the overlay is on, and so
// Apollo caches polygons across pans — a municipality's shape never
// changes within a session.
export const GET_PLACES_GEOMETRY = gql(/* GraphQL */ `
  query GetPlacesGeometry($ids: [uuid!]!) {
    places(where: { id: { _in: $ids } }) {
      id
      name
      simplified_geom
    }
  }
`);

// MLS access checking (used by ListingOverview in non-MLS-drawer context)
export const GET_MLS_ACCESS = gql(/* GraphQL */ `
  query GetMlsAccess($lotId: uuid!, $teamId: uuid) {
    existing_share: mls_shares(
      where: {
        lot_id: { _eq: $lotId }
        team_id: { _eq: $teamId }
        status: { _eq: "shared" }
      }
      limit: 1
    ) {
      id
    }
    pending_request: mls_shares(
      where: {
        lot_id: { _eq: $lotId }
        team_id: { _eq: $teamId }
        status: { _eq: "pending" }
      }
      limit: 1
    ) {
      id
    }
    my_website_portal: teams_portals(
      where: {
        team_id: { _eq: $teamId }
        portal: { type: { _in: [idx, shared] } }
      }
      limit: 1
    ) {
      portal {
        id
        name
        type
      }
    }
    existing_portal_listings: portal_listings(
      where: { lot_id: { _eq: $lotId } }
    ) {
      id
      portal_id
      live
    }
    lot_brochure_templates: doc_templates(
      limit: 100
      where: { type: { _eq: lot_brochure }, active: { _eq: true } }
    ) {
      id
      name
      active
      ...DocTemplateDefaultLinks
    }
  }
`);

// Get team MLS settings
export const GET_TEAM_MLS_SETTINGS = gql(/* GraphQL */ `
  query GetTeamMlsSettings($teamId: uuid!) {
    settings: mls_settings(where: { team_id: { _eq: $teamId } }) {
      id
      team_id
      tenant_id
      auto_publish_shared_listings
      auto_match_with_buyers
      auto_matching_email_template_id
      auto_matching_email_template {
        id
        name
      }
    }
  }
`);

// =============================================================================
// MUTATIONS
// =============================================================================

// Update a subset of MLS fields on a lot — each handler passes only the fields it owns
// to avoid stale-read overwrites when two mutations fire in the same tick.
export const UPDATE_LOT_MLS_FIELD = gql(/* GraphQL */ `
  mutation UpdateLotMlsField($lotId: uuid!, $changes: lots_set_input!) {
    update_lots_by_pk(pk_columns: { id: $lotId }, _set: $changes) {
      id
      shared_on_mls_at
      mls_share_mode
      mls_terms_enabled
      mls_commission_type
      mls_buyer_agent_share_percent
      mls_buyer_agent_share_fixed_amount
      mls_note
    }
  }
`);

// Update listing MLS settings (toggle, share mode, commission)
export const UPDATE_LISTING_MLS_SETTINGS = gql(/* GraphQL */ `
  mutation UpdateListingMlsSettings(
    $lotId: uuid!
    $sharedOnMlsAt: timestamptz
    $mlsShareMode: String
    $mlsTermsEnabled: Boolean
    $mlsCommissionType: mls_commission_type_enum_enum
    $mlsBuyerAgentSharePercent: numeric
    $mlsBuyerAgentShareFixedAmount: numeric
    $mlsNote: String
  ) {
    update_lots_by_pk(
      pk_columns: { id: $lotId }
      _set: {
        shared_on_mls_at: $sharedOnMlsAt
        mls_share_mode: $mlsShareMode
        mls_terms_enabled: $mlsTermsEnabled
        mls_commission_type: $mlsCommissionType
        mls_buyer_agent_share_percent: $mlsBuyerAgentSharePercent
        mls_buyer_agent_share_fixed_amount: $mlsBuyerAgentShareFixedAmount
        mls_note: $mlsNote
      }
    ) {
      id
      shared_on_mls_at
      mls_share_mode
      mls_terms_enabled
      mls_commission_type
      mls_buyer_agent_share_percent
      mls_buyer_agent_share_fixed_amount
      mls_note
    }
  }
`);

// Share listing with specific team (insert with status='shared')
export const SHARE_LISTING_WITH_TEAM = gql(/* GraphQL */ `
  mutation ShareListingWithTeam(
    $lotId: uuid!
    $teamId: uuid!
    $sharedByUserId: uuid!
  ) {
    insert_mls_shares_one(
      object: {
        lot_id: $lotId
        team_id: $teamId
        shared_by_user_id: $sharedByUserId
        status: "shared"
      }
      on_conflict: {
        constraint: mls_shares_lot_id_team_id_key
        update_columns: [status, shared_by_user_id]
      }
    ) {
      id
      lot_id
      team_id
      status
      created_at
    }
  }
`);

// Remove listing share
export const REMOVE_LISTING_SHARE = gql(/* GraphQL */ `
  mutation RemoveListingShare($shareId: uuid!) {
    delete_mls_shares_by_pk(id: $shareId) {
      id
    }
  }
`);

// Request MLS listing access (insert with status='pending')
export const REQUEST_MLS_LISTING_ACCESS = gql(/* GraphQL */ `
  mutation RequestMlsListingAccess(
    $lotId: uuid!
    $requestingTeamId: uuid!
    $requestingUserId: uuid!
  ) {
    insert_mls_shares_one(
      object: {
        lot_id: $lotId
        team_id: $requestingTeamId
        requested_by_user_id: $requestingUserId
        status: "pending"
      }
      on_conflict: {
        constraint: mls_shares_lot_id_team_id_key
        update_columns: []
      }
    ) {
      id
      lot_id
      team_id
      status
      created_at
    }
  }
`);

// Handle MLS share request (approve/reject) — single update, no separate insert needed
export const HANDLE_MLS_SHARE_REQUEST = gql(/* GraphQL */ `
  mutation HandleMlsShareRequest(
    $requestId: uuid!
    $status: String!
    $handledByUserId: uuid!
  ) {
    update_mls_shares_by_pk(
      pk_columns: { id: $requestId }
      _set: {
        status: $status
        shared_by_user_id: $handledByUserId
        handled_at: "now()"
      }
    ) {
      id
      status
      handled_at
      lot_id
      team_id
    }
  }
`);

// Update team MLS settings
export const UPDATE_TEAM_MLS_SETTINGS = gql(/* GraphQL */ `
  mutation UpdateTeamMlsSettings(
    $teamId: uuid!
    $tenantId: uuid!
    $autoPublishSharedListings: Boolean
    $autoMatchWithBuyers: Boolean
    $autoMatchingEmailTemplateId: uuid
  ) {
    insert_mls_settings_one(
      object: {
        team_id: $teamId
        tenant_id: $tenantId
        auto_publish_shared_listings: $autoPublishSharedListings
        auto_match_with_buyers: $autoMatchWithBuyers
        auto_matching_email_template_id: $autoMatchingEmailTemplateId
      }
      on_conflict: {
        constraint: mls_settings_team_id_key
        update_columns: [
          auto_publish_shared_listings
          auto_match_with_buyers
          auto_matching_email_template_id
        ]
      }
    ) {
      id
      team_id
      auto_publish_shared_listings
      auto_match_with_buyers
      auto_matching_email_template_id
    }
  }
`);

// Listing data needed to compute default buyer-matching criteria on the MLS
// side. mls_lots / mls_properties are the cross-tenant-safe surfaces; the
// underlying lots / properties tables are RLS-restricted and unreadable by
// non-owner agents.
export const GET_MLS_LOT_FOR_MATCHING = gql(/* GraphQL */ `
  query GetMlsLotForMatching($lotId: uuid!) {
    mls_lots(where: { id: { _eq: $lotId } }, limit: 1) {
      id
      offer_type
      sale_price
      rent_net
      price_unit
      property {
        id
        # Session-gated: only the levels the caller may already see.
        places {
          id
          type
        }
        property_type {
          id
          composite_type_object {
            id
            name
          }
        }
      }
    }
    get_surrounding_listing_places(
      listingId: $lotId
      placeType: "locality"
      source: "mls_lot"
    ) {
      place_id
    }
  }
`);

// Per-share buyer matching criteria — saved by the team that has access to the
// MLS-shared listing. Owners use the lot's own auto_matching_filters instead.
export const GET_MLS_SHARE_MATCHING_CRITERIA = gql(/* GraphQL */ `
  query GetMlsShareMatchingCriteria($shareId: uuid!) {
    mls_shares_by_pk(id: $shareId) {
      id
      auto_matching_filters
    }
  }
`);

// Save buyer-matching criteria as a non-owner team member. Goes through
// the `upsert_mls_share_criteria` Postgres function (exposed as a Hasura
// mutation, SECURITY DEFINER) rather than a direct `insert_mls_shares_one`
// + on_conflict path. Rationale:
//
// - The mls_shares UPDATE permission is owner-only. on_conflict in
//   Hasura v2.48+ evaluates UPDATE permission's filter on the existing
//   row, so a non-owner would get 0 rows back.
// - We can't safely widen UPDATE permission to non-owner teams because
//   that would also expose `status`, `handled_at`, and `shared_by_user_id`
//   to a non-owner. A column-level trigger guard wouldn't fire on Hasura
//   sessions (Hasura doesn't set `my.user_id`).
//
// The function reads `hasura_session`, validates the caller's team has
// access (everyone-mode lot in MLS network OR existing `status='shared'`
// row for one of their teams), and performs the upsert. Owners are
// allowed too — the function falls back to the same upsert and ON CONFLICT
// only rewrites `auto_matching_filters`.
export const UPSERT_MLS_SHARE_MATCHING_CRITERIA = gql(/* GraphQL */ `
  mutation UpsertMlsShareMatchingCriteria($lotId: uuid!, $filters: jsonb) {
    upsert_mls_share_criteria(args: { lot_id: $lotId, filters: $filters }) {
      id
      auto_matching_filters
    }
  }
`);

// Publish MLS listing to website (creates portal_listing)
export const PUBLISH_MLS_LISTING_TO_WEBSITE = gql(/* GraphQL */ `
  mutation PublishMlsListingToWebsite(
    $lotId: uuid!
    $portalId: uuid!
    $live: Boolean!
  ) {
    insert_portal_listings_one(
      object: { lot_id: $lotId, portal_id: $portalId, live: $live }
      on_conflict: {
        constraint: portal_listings_portal_id_lot_id_key
        update_columns: [live]
      }
    ) {
      id
      lot_id
      portal_id
      live
    }
  }
`);
